import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Eye, Loader2, FolderLock, FolderOpen, CheckCircle2, FileDown, FileText, Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { fmtLog } from "@/lib/tz";

const fmtTs = (ts) => (ts ? fmtLog(ts) : "");

export function DocumentAccessLog({ participant, refreshKey }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dl, setDl] = useState("");
  const [filters, setFilters] = useState({ worker: "all", kind: "all", date_from: "", date_to: "" });

  const params = useMemo(() => {
    const p = {};
    if (filters.worker !== "all") p.worker = filters.worker;
    if (filters.kind !== "all") p.kind = filters.kind;
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    return p;
  }, [filters]);

  // Worker options come from the unfiltered set so the dropdown stays stable.
  const [workerOpts, setWorkerOpts] = useState([]);
  useEffect(() => {
    api.participantDocumentViews(participant)
      .then((r) => {
        const m = new Map();
        (r || []).forEach((x) => { if (x.worker) m.set(x.worker, x.worker_name || x.worker); });
        setWorkerOpts([...m.entries()].map(([id, name]) => ({ id, name })));
      })
      .catch(() => setWorkerOpts([]));
  }, [participant, refreshKey]);

  useEffect(() => {
    setLoading(true);
    api.participantDocumentViews(participant, params)
      .then((r) => setRows(r || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [participant, refreshKey, params]);

  const doExport = async (format) => {
    setDl(format);
    try {
      const blob = await api.documentViewsExport(participant, format, params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${participant}_document_access_log.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed."); }
    finally { setDl(""); }
  };

  const hasFilters = filters.worker !== "all" || filters.kind !== "all" || filters.date_from || filters.date_to;
  const clear = () => setFilters({ worker: "all", kind: "all", date_from: "", date_to: "" });

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="document-access-log">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <Eye className="h-4 w-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-800">Worker document access log</h3>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => doExport("csv")} disabled={dl === "csv" || rows.length === 0} data-testid="access-log-export-csv">
            {dl === "csv" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><FileDown className="h-3.5 w-3.5 mr-1" />CSV</>}
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => doExport("pdf")} disabled={dl === "pdf" || rows.length === 0} data-testid="access-log-export-pdf">
            {dl === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><FileText className="h-3.5 w-3.5 mr-1" />PDF</>}
          </Button>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Records which support worker opened which document in the worker app, and when. A green check means the worker acknowledged reading it. Exports honour the filters below.
      </p>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-2 rounded-lg bg-slate-50 border border-slate-100 p-2.5 mb-3" data-testid="access-log-filters">
        <div className="flex items-center gap-1 text-[11px] font-semibold text-slate-500"><Filter className="h-3.5 w-3.5" /></div>
        <div>
          <label className="block text-[10px] font-semibold text-slate-500 mb-0.5">Worker</label>
          <Select value={filters.worker} onValueChange={(v) => setFilters((f) => ({ ...f, worker: v }))}>
            <SelectTrigger className="h-8 text-xs w-40" data-testid="access-filter-worker"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">All workers</SelectItem>
              {workerOpts.map((w) => <SelectItem key={w.id} value={w.id} className="text-xs">{w.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-slate-500 mb-0.5">Type</label>
          <Select value={filters.kind} onValueChange={(v) => setFilters((f) => ({ ...f, kind: v }))}>
            <SelectTrigger className="h-8 text-xs w-32" data-testid="access-filter-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">All types</SelectItem>
              <SelectItem value="internal" className="text-xs">Internal</SelectItem>
              <SelectItem value="external" className="text-xs">External</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-slate-500 mb-0.5">From</label>
          <Input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} className="h-8 text-xs w-36" data-testid="access-filter-from" />
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-slate-500 mb-0.5">To</label>
          <Input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} className="h-8 text-xs w-36" data-testid="access-filter-to" />
        </div>
        {hasFilters && (
          <Button size="sm" variant="ghost" className="h-8 text-xs text-slate-500" onClick={clear} data-testid="access-filter-clear">
            <X className="h-3.5 w-3.5 mr-1" />Clear
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-slate-500 py-4 text-center" data-testid="document-access-log-empty">
          {hasFilters ? "No document views match these filters." : "No document views recorded yet."}
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center gap-3 py-2" data-testid={`doc-view-${r.id}`}>
              {r.kind === "external"
                ? <FolderOpen className="h-4 w-4 text-sky-500 shrink-0" title="External document" />
                : <FolderLock className="h-4 w-4 text-emerald-600 shrink-0" title="Internal document" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-800 truncate flex items-center gap-1.5">
                  {r.doc_title || "Document"}
                  {r.acknowledged && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-600 font-medium" title={`Acknowledged ${fmtTs(r.acknowledged_at)}`} data-testid={`doc-view-ack-${r.id}`}>
                      <CheckCircle2 className="h-3 w-3" /> read
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500">
                  <span className="font-medium text-slate-600">{r.worker_name || r.worker || "Worker"}</span>
                  {" · "}
                  <span className={r.kind === "external" ? "text-sky-600" : "text-emerald-600"}>
                    {r.kind === "external" ? "External" : "Internal"}
                  </span>
                </div>
              </div>
              <div className="text-[11px] text-slate-400 whitespace-nowrap shrink-0">{fmtTs(r.ts)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
